import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoginService } from '../../service/login.service';
import { UserService } from '../../service/user.service';
import { NgClass, NgIf } from '@angular/common';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { AlertPopupComponent } from '../popups/alert-popup/alert-popup.component';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [FormsModule, NgIf, NgClass, ReactiveFormsModule, SpinnerComponent, AlertPopupComponent],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.css'
})
export class ChangePasswordComponent implements OnInit {
  oldPassword: string = '';
  newPassword: string = ''
  confirmNewPassword: string = '';
  form!: FormGroup;
  loading = false;
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  passwordMismatch = false;
  public alertData = { alertMessage: '', alertVisible: '' };
  passwordStrength: number = 0;
  passwordFeedback: string = '';

  constructor(private loginService: LoginService, private userService: UserService, private fb: FormBuilder, private router: Router) {
    this.form = this.fb.group({
      oldPassword: ['', [Validators.required, Validators.minLength(8)]],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmNewPassword: ['', [Validators.required, Validators.minLength(8)]],
    });
  }
  ngOnInit(): void {
    const showPopup = sessionStorage.getItem('showChangePasswordPopup') === '1';
    if (showPopup) {
      this.alertData = {
        alertMessage: 'Your password is weak or uses a default password. Please change to a strong password to ensure security.',
        alertVisible: 'info'
      };
      // Note: the flag is cleared in closeAlert(), not here. When arriving from a page where the
      // layout is visible (SSO/Auth/client-portal), this component is instantiated twice — once in
      // the layout's <router-outlet> and once via *ngIf in app.component. Removing the flag on read
      // lets the first (throwaway) instance consume it, so the visible instance never shows the popup.
    }
  }

  @ViewChild('newPasswordInput') newPasswordInput!: ElementRef;
  @ViewChild('confirmPasswordInput') confirmPasswordInput!: ElementRef;
  @ViewChild('SubmitPasswordInput') SubmitPasswordInput!: ElementRef;
  focusNewPassword(event: Event) {
    event.preventDefault();
    setTimeout(() => {
      this.newPasswordInput.nativeElement.focus();
    });
  }

  focusConfirmPassword(event: Event) {
    event.preventDefault();
    setTimeout(() => {
      this.confirmPasswordInput.nativeElement.focus();
    });
  }
  focusSubmitPassword(event: Event) {
    event.preventDefault();
    setTimeout(() => {
      this.SubmitPasswordInput.nativeElement.focus();
    });
  }
  // strengthCheck(password: string): string {
  //   if (!password) return ''; 

  //   const result = (window as any).zxcvbn(password); 
  //   const score = result.score;

  //   let strength = 'Weak'; 
  //   if (score === 3) strength = 'Medium';
  //   else if (score === 4) strength = 'Strong';

  //   return strength;
  // }
  checkNewPassword() {
    const newPasswordControl = this.form.get('newPassword');
    const confirmPasswordControl = this.form.get('confirmNewPassword');
    if (!newPasswordControl?.value && confirmPasswordControl?.value && !newPasswordControl?.touched) {
      newPasswordControl?.markAsTouched();
    }

  }
  strengthCheck(password: string): string {
    if (!password) return '';

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{}|;:'"\\,.<>\/\?`~]/.test(password);
    const isValidLength = password.length >= 8 && password.length <= 15;

    if (!isValidLength) {
      return 'Password should contain at least 8 to 15 characters.';
    }

    if (!hasUpperCase) {
      return 'Password should contain at least one uppercase letter.';
    }

    if (!hasLowerCase) {
      return 'Password should contain at least one lowercase letter.';
    }

    if (!hasNumber) {
      return 'Password should contain at least one numeric value.';
    }

    if (!hasSpecialChar) {
      return 'Password should contain at least one special character.';
    }

    return ''; // No errors, password is valid
  }

  getPasswordStrengthClass(password: string) {
    const strength = this.strengthCheck(password);
    return {
      'weak-password': strength === 'Weak',
      'medium-password': strength === 'Medium',
      'strong-password': strength === 'Strong'
    };
  }
  getPasswordStrengthClasstextbox(password: string) {
    const strength = this.strengthCheck(password);
    return {
      'is-invalid': strength === 'Weak',
      'medium-password-textbox': strength === 'Medium',
      'strong-password-textbox': strength === 'Strong'
    };
  }
  toggleOldPasswordVisibility(): void {
    this.showOldPassword = !this.showOldPassword;
    setTimeout(() => {
      this.showOldPassword = false;
    }, 5000);
  }
  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
    setTimeout(() => {
      this.showNewPassword = false;
    }, 5000);
  }
  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
    setTimeout(() => {
      this.showConfirmPassword = false;
    }, 5000);
  }
  onSubmit() {
    const userId = this.userService.getUserId();
    const newPassword = this.form.get('newPassword')?.value;
    const confirmNewPassword = this.form.get('confirmNewPassword')?.value;
    if (this.form.valid) {
      if (this.strengthCheck(newPassword) != '') {
        return;
      }
      if (newPassword !== confirmNewPassword) {
        this.passwordMismatch = true;
        return;
      } else {
        this.passwordMismatch = false;
      }
      this.loading = true;
      this.loginService.ChangePassword(userId, this.form.value.newPassword, this.form.value.oldPassword).subscribe({
        next: (response) => {
          if (response.status === 200) {
            const data = response.body.res;
            if (data == 'Success') {
              this.loading = false;
              this.alertData = { alertMessage: 'Password Changed Successfully', alertVisible: 'success' };

            } else if (data == 'Old Password and New Password should not be same.') {
              this.loading = false;
              this.alertData = { alertMessage: 'New Password can not be same as Old Password. Please enter different password.', alertVisible: 'error' };
            } else if (data == 'Current password you entered is incorrect. Please check.') {
              this.loading = false;
              this.alertData = { alertMessage: 'Current password you entered is incorrect. Please check.', alertVisible: 'error' };
            } else {
              this.loading = false;
              this.alertData = { alertMessage: 'Failed to change password. Please try again.', alertVisible: 'error' };
            }
          } else {
            this.loading = false;
            this.alertData = { alertMessage: 'Failed to change password. Please try again.', alertVisible: 'error' };
          }
        },
        error: (err) => {
          this.loading = false;
          if (err.status === 400 && err.error?.result) {
            this.alertData = { alertMessage: err.error.result, alertVisible: 'error' };
          } else {
            this.alertData = { alertMessage: 'Failed to change password. Please try again.', alertVisible: 'error' };
          }
        }
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  logOut() {
    sessionStorage.clear();
    if (environment.isLogoOutFromLive === '1') {
      window.location.href = 'https://login.teamleaseregtech.com/logout.aspx';
    } else {
      this.router.navigate(['/Login']);
    }

  }
  onClear() {
    this.passwordMismatch = false;
    this.oldPassword = '';
    this.newPassword = '';
    this.confirmNewPassword = '';
    this.form.reset();
    this.form = this.fb.group({
      oldPassword: ['', [Validators.required, Validators.minLength(8)]],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmNewPassword: ['', [Validators.required, Validators.minLength(8)]],
    });
    this.form.markAsUntouched();
    this.form.updateValueAndValidity();
    this.showNewPassword = false;
    this.showOldPassword = false;
    this.showConfirmPassword = false;
  }
  closeAlert() {
    if (this.alertData.alertVisible == 'success') {
      this.onClear();
      this.logOut();
    }
    sessionStorage.removeItem('showChangePasswordPopup');
    this.alertData = { alertMessage: '', alertVisible: '' };
  }
  goBack() {
    window.history.back();
  }
}
